import type { GridLayouts } from '../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { SINK_REGISTRY } from './Sinks/SinkRegistry.js'

const COLS: Record<keyof GridLayouts, number> = { lg: 12, md: 10, sm: 8, xs: 6, xxs: 4 }

/**
 * Produces the layout react-grid-layout renders: adds a default entry for any panel that doesn't
 * yet have one, and heals zero/undefined sizes. New panels are placed at `y: Infinity`; RGL
 * compacts them and echoes the result back through `onLayoutChange`, which is what persists the
 * final positions. The seeder must therefore be the ONLY thing that places new panels — a second
 * writer with a different placement fights RGL's controlled-layout echo forever (SPCFY-FRONTEND-J5).
 */
export function seedLayoutsWithDefaults(
  base: GridLayouts,
  panels: Array<{ panelId: string; kindKey: keyof typeof SINK_REGISTRY }>
): GridLayouts {
  const seeded: GridLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] }

  // quick lookup of defaults per panel
  const defs = new Map(
    panels.map((p) => {
      const d = SINK_REGISTRY[p.kindKey]?.defaults ?? { w: 4, h: 14, minW: 3, minH: 12 }
      return [p.panelId, d]
    })
  )

  Object.keys(base).forEach((bp) => {
    const colCount = COLS[bp as keyof GridLayouts]
    const arr = (base[bp as keyof GridLayouts] ?? []).slice()
    const existing = new Set(arr.map((i) => i.i))

    // add missing panels at this breakpoint
    for (const p of panels) {
      if (existing.has(p.panelId)) continue
      const d = defs.get(p.panelId)!
      const idx = arr.length
      const w = Math.min(d.w ?? 4, colCount)
      arr.push({
        i: p.panelId,
        x: (idx * w) % colCount,
        y: Infinity,
        w,
        h: d.h ?? 14,
        minW: d.minW ?? 3,
        minH: d.minH ?? 12,
        maxW: d.maxW,
        maxH: d.maxH
      })
    }

    // also “heal” any zero/undefined sizes in base (just in case)
    seeded[bp as keyof GridLayouts] = arr.map((it) => {
      const d = defs.get(it.i)
      if (!d) return it
      const w = Math.max(1, Math.min(it.w || d.w || 4, colCount))
      let x = typeof it.x === 'number' ? it.x : 0
      if (x + w > colCount) x = Math.max(0, colCount - w)
      return {
        ...it,
        w,
        h: it.h || d.h || 14,
        x,
        y: typeof it.y === 'number' ? it.y : Infinity,
        minW: it.minW ?? d.minW ?? 3,
        minH: it.minH ?? d.minH ?? 12,
        maxW: it.maxW ?? d.maxW,
        maxH: it.maxH ?? d.maxH
      }
    })
  })

  return seeded
}
